import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Badge,
  Icon,
  Box,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import {
  BsSnow,
  BsCloudLightningRain,
  BsCloudRainHeavy,
  BsEyeSlash,
  BsWind,
  BsThermometerSnow,
  BsThermometerHigh,
  BsSnow2,
  BsCheckCircle,
  BsExclamationTriangleFill,
} from 'react-icons/bs';
import {
  WarningTypes,
  WarningSeverity,
  warningMessages,
} from '../../../../../config/weatherWarnings';

const getWarningIcon = type => {
  switch (type) {
    case WarningTypes.SNOW:
      return BsSnow;
    case WarningTypes.STORM:
      return BsCloudLightningRain;
    case WarningTypes.HEAVY_RAIN:
      return BsCloudRainHeavy;
    case WarningTypes.FOG:
      return BsEyeSlash;
    case WarningTypes.STRONG_WIND:
      return BsWind;
    case WarningTypes.ICE:
      return BsSnow2;
    case WarningTypes.EXTREME_COLD:
      return BsThermometerSnow;
    case WarningTypes.EXTREME_HEAT:
      return BsThermometerHigh;
    default:
      return BsExclamationTriangleFill;
  }
};

const getSeverityColor = severity => {
  switch (severity) {
    case WarningSeverity.EXTREME:
      return 'red';
    case WarningSeverity.DANGER:
      return 'orange';
    case WarningSeverity.WARNING:
      return 'yellow';
    case WarningSeverity.INFO:
      return 'blue';
    default:
      return 'gray';
  }
};

const getSeverityText = severity => {
  switch (severity) {
    case WarningSeverity.EXTREME:
      return 'Extrema';
    case WarningSeverity.DANGER:
      return 'Peligro';
    case WarningSeverity.WARNING:
      return 'Precaución';
    case WarningSeverity.INFO:
      return 'Información';
    default:
      return '';
  }
};

const getSafetyTips = type => {
  switch (type) {
    case WarningTypes.SNOW:
      return [
        'Reduce la velocidad y aumenta la distancia de seguridad',
        'Lleva cadenas para la nieve en el vehículo',
        'Evita adelantamientos innecesarios',
        'Usa luces de cruce para mejorar la visibilidad',
        'Ten el depósito de combustible lleno',
      ];
    case WarningTypes.STORM:
      return [
        'Evita conducir durante la tormenta si es posible',
        'Mantente alejado de árboles y postes',
        'Si estás en el coche, quédate dentro con ventanas cerradas',
        'Reduce la velocidad y aumenta la distancia de seguridad',
        'Enciende las luces de emergencia si la visibilidad es muy baja',
      ];
    case WarningTypes.HEAVY_RAIN:
      return [
        'Reduce la velocidad para evitar hidroplaneo',
        'Aumenta la distancia de seguridad',
        'Usa las luces de cruce',
        'Evita charcos grandes que puedan ocultar baches',
        'Ten cuidado con la menor adherencia al asfalto',
      ];
    case WarningTypes.FOG:
      return [
        'Reduce la velocidad significativamente',
        'Usa las luces antiniebla delanteras y traseras',
        'No uses las luces largas (reflejan en la niebla)',
        'Mantén mayor distancia de seguridad',
        'Usa las marcas del arcén como referencia',
      ];
    case WarningTypes.STRONG_WIND:
      return [
        'Sujeta el volante con firmeza',
        'Reduce la velocidad, especialmente en vehículos altos',
        'Ten especial cuidado al adelantar o ser adelantado',
        'Evita circular cerca de camiones y autobuses',
        'Ten cuidado en tramos desprotegidos y puentes',
      ];
    case WarningTypes.ICE:
      return [
        'Conduce muy despacio y con suavidad',
        'Evita frenazos y aceleraciones bruscas',
        'Ten especial cuidado en puentes y zonas de sombra',
        'Mantén una distancia muy amplia de seguridad',
        'Lleva equipamiento de invierno (cadenas, rascador)',
      ];
    case WarningTypes.EXTREME_COLD:
      return [
        'Verifica el líquido anticongelante del vehículo',
        'Ten mantas y ropa de abrigo en el coche',
        'Lleva un kit de emergencia',
        'Mantén el depósito lleno para evitar congelación de combustible',
        'Revisa el estado de la batería',
      ];
    case WarningTypes.EXTREME_HEAT:
      return [
        'Lleva agua abundante en el vehículo',
        'Verifica los niveles de refrigerante',
        'Evita las horas de más calor si es posible',
        'Haz pausas frecuentes en lugares con sombra',
        'Ten cuidado con el sobrecalentamiento del motor',
      ];
    default:
      return [
        'Conduce con precaución',
        'Mantén atención constante a las condiciones',
      ];
  }
};

export default function WeatherWarningDetailModal({
  isOpen,
  onClose,
  warning,
  hourPeriod,
  localityName,
}) {
  if (!warning) return null;

  const messages = warningMessages[warning.type];
  const warningIcon = getWarningIcon(warning.type);
  const severityColor = getSeverityColor(warning.severity);
  const safetyTips = getSafetyTips(warning.type);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={3}>
            <Icon as={warningIcon} boxSize={6} color={`${severityColor}.500`} />
            <VStack align="start" spacing={0}>
              <Text>{messages?.title || 'Alerta Meteorológica'}</Text>
              {hourPeriod && (
                <Text fontSize="sm" fontWeight="normal" color="gray.600">
                  Hora: {`${hourPeriod}:00`}
                </Text>
              )}
              {localityName && (
                <Text fontSize="sm" fontWeight="normal" color="gray.600">
                  {localityName}
                </Text>
              )}
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Severity Badge */}
            <HStack>
              <Badge
                colorScheme={severityColor}
                fontSize="md"
                px={3}
                py={1}
                borderRadius="full"
              >
                Nivel: {getSeverityText(warning.severity)}
              </Badge>
            </HStack>

            {/* Warning Message */}
            <Alert
              status={
                warning.severity === WarningSeverity.EXTREME ||
                warning.severity === WarningSeverity.DANGER
                  ? 'error'
                  : 'warning'
              }
              borderRadius="md"
            >
              <AlertIcon />
              <Box>
                <AlertTitle>Advertencia</AlertTitle>
                <AlertDescription>
                  {messages?.[warning.severity] ||
                    'Se han detectado condiciones meteorológicas adversas.'}
                </AlertDescription>
              </Box>
            </Alert>

            {/* Additional Details */}
            {(warning.temperature ||
              warning.windSpeed ||
              warning.windGusts ||
              warning.precipitation ||
              warning.probability) && (
              <Box
                p={3}
                bg="gray.50"
                borderRadius="md"
                borderLeft="4px"
                borderColor={`${severityColor}.400`}
              >
                <Text fontWeight="semibold" mb={2}>
                  Detalles:
                </Text>
                <VStack align="start" spacing={1}>
                  {warning.temperature && (
                    <Text fontSize="sm">
                      🌡️ Temperatura: {warning.temperature}°C
                    </Text>
                  )}
                  {warning.windSpeed && (
                    <Text fontSize="sm">
                      💨 Viento: {warning.windSpeed} km/h
                    </Text>
                  )}
                  {warning.windGusts && (
                    <Text fontSize="sm">
                      💨 Rachas: {warning.windGusts} km/h
                    </Text>
                  )}
                  {warning.precipitation && (
                    <Text fontSize="sm">
                      🌧️ Precipitación: {warning.precipitation} mm
                    </Text>
                  )}
                  {warning.probability && (
                    <Text fontSize="sm">
                      📊 Probabilidad: {warning.probability}%
                    </Text>
                  )}
                </VStack>
              </Box>
            )}

            {/* Safety Tips */}
            <Box>
              <Text fontWeight="semibold" mb={2} fontSize="lg">
                ⚠️ Consejos de seguridad:
              </Text>
              <List spacing={2}>
                {safetyTips.map((tip, index) => (
                  <ListItem key={index} fontSize="sm">
                    <HStack align="start">
                      <ListIcon
                        as={BsCheckCircle}
                        color={`${severityColor}.500`}
                        mt={0.5}
                      />
                      <Text>{tip}</Text>
                    </HStack>
                  </ListItem>
                ))}
              </List>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="blue" onClick={onClose}>
            Entendido
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
